import { t } from '@/i18n';
import { z } from 'zod';

export const TableNestSettingModel = z.object({
  project: z.boolean(),
  policyLevel1: z.boolean().optional(), // TODO `optional` is KS and SS only
  policyLevel2: z.boolean().optional(), // TODO `optional` is KS and SS only
  policyLevel3: z.boolean().optional(), // TODO this field is RS only
});

export const TableNestLevelLabelModifier = {
  project: t('project'),
  policyLevel1: t('policy-level-1'),
  policyLevel2: t('policy-level-2'),
  policyLevel3: t('policy-level-3'),
} as const satisfies Record<
  keyof z.infer<typeof TableNestSettingModel>,
  string
>;

export type ProjectTableColumn =
  | 'name'
  | 'fiscalYear'
  | 'responsibleOrganizationName'
  | 'accountingName'
  | 'expenditurePurposeLabelList'
  | 'budgetType'
  | 'budgetData'
  | 'overview'
  | 'sortingCode'
  | 'priority'
  | 'status'
  | 'frameAllocationCategory'
  | 'frameAllocationCurrentYearCurrentBudgetData'
  | 'frameAllocationTotalRevenueNationalTreasuryDisbursements'
  | 'frameAllocationTotalRevenueLocalGovernmentBondsRequested'
  | 'frameAllocationTotalRevenueOthers'
  | 'frameAllocationTotalRevenueGeneral'
  | 'frameAllocationBreakdownLabelAmount'
  | 'frameAllocationCategoryRemainingAmount'
  | 'currentYearLatest'
  | 'totalSpecificRevenueLatest'
  | 'totalRevenueNationalTreasuryDisbursementsLatest'
  | 'totalRevenueLocalGovernmentBondsLatest'
  | 'totalRevenueOthersLatest'
  | 'totalRevenueGeneralLatest'
  | 'totalExpenditureLatest'
  | 'totalSpecificExpenditureLatest'
  | 'totalExpenditureNationalTreasuryDisbursementsLatest'
  | 'totalExpenditureLocalGovernmentBondsLatest'
  | 'totalExpenditureOthersLatest'
  | 'totalExpenditureGeneralLatest'
  | 'currentYearCurrentBudgetData'
  | 'totalSpecificRevenue'
  | 'totalRevenueNationalTreasuryDisbursements'
  | 'totalRevenueLocalGovernmentBondsRequested'
  | 'totalRevenueOthers'
  | 'totalRevenueGeneral'
  | 'totalExpenditureRequested'
  | 'totalSpecificExpenditure'
  | 'totalExpenditureNationalTreasuryDisbursements'
  | 'totalExpenditureLocalGovernmentBondsRequested'
  | 'totalExpenditureOthers'
  | 'totalExpenditureGeneral'
  | 'currentYearAssessed'
  | 'totalSpecificRevenueAssessed'
  | 'totalRevenueNationalTreasuryDisbursementsAssessed'
  | 'totalRevenueLocalGovernmentBondsAssessed'
  | 'totalRevenueOthersAssessed'
  | 'totalRevenueGeneralAssessed'
  | 'totalExpenditureAssessed'
  | 'totalSpecificExpenditureAssessed'
  | 'totalExpenditureNationalTreasuryDisbursementsAssessed'
  | 'totalExpenditureLocalGovernmentBondsAssessed'
  | 'totalExpenditureOthersAssessed'
  | 'totalExpenditureGeneralAssessed'
  | 'currentYearNotified'
  | 'totalSpecificRevenueNotified'
  | 'totalRevenueNationalTreasuryDisbursementsNotified'
  | 'totalRevenueLocalGovernmentBondsNotified'
  | 'totalRevenueOthersNotified'
  | 'totalRevenueGeneralNotified'
  | 'selectedYearDecidedBudgetNotified'
  | 'previousFiscalYearInitialBudget'
  | 'totalExpenditureNotified'
  | 'totalSpecificExpenditureNotified'
  | 'totalExpenditureNationalTreasuryDisbursementsNotified'
  | 'totalExpenditureLocalGovernmentBondsNotified'
  | 'totalExpenditureOthersNotified'
  | 'totalExpenditureGeneralNotified'
  | 'totalDiscontinuedAmount'
  | 'totalDiscontinuedAmountOfSpecialRevenue'
  | 'discontinuedAmountOfNationalTreasuryDisbursement'
  | 'discontinuedAmountOfLocalGovernmentBond'
  | 'discontinuedAmountOfOthers'
  | 'discontinuedAmountOfGeneral';

export const PoliciesTableColumnLabelModifier = {
  policyLevel1Name: t('policy-level-1'),
  policyLevel1Level: t('level'),
  policyLevel2Name: t('policy-level-2'),
  policyLevel2Level: t('level'),
  policyLevel3Name: t('policy-level-3'),
  policyLevel3Level: t('level'),
};

export type ProjectTableColumnCategory =
  | 'basicInformation'
  | 'revenueLatest'
  | 'expenditureLatest'
  | 'revenueRequested'
  | 'expenditureRequested'
  | 'revenueAssessed'
  | 'expenditureAssessed'
  | 'revenueNotified'
  | 'expenditureNotified'
  | 'discontinuedAmount';

export const ProjectTableColumnCategoryLabelModifier = {
  basicInformation: t('basic-information'),
  revenueLatest: t('column-category-revenue-latest'),
  expenditureLatest: t('column-category-expenditure-latest'),
  revenueRequested: t('column-category-revenue-requested'),
  expenditureRequested: t('column-category-expenditure-requested'),
  revenueAssessed: t('column-category-revenue-assessed'),
  expenditureAssessed: t('column-category-expenditure-assessed'),
  revenueNotified: t('column-category-revenue-notified'),
  expenditureNotified: t('column-category-expenditure-notified'),
  discontinuedAmount: t('column-category-discontinued-amount'),
} as const satisfies Record<ProjectTableColumnCategory, string>;

export const administrativeReformTeamEvaluationOptions = z.enum([
  'false',
  'true',
]);

export const AdministrativeReformTeamEvaluationOptionLabelModifier = {
  false: t('filter-by-non-evaluation-skip'),
  true: t('filter-by-evaluation-skip'),
} as const satisfies Record<
  z.infer<typeof administrativeReformTeamEvaluationOptions>,
  string
>;
